// src/styles/theme.ts
import { tokens } from './tokens'

export type AppTheme = {
  colors: {
    text: string
    subText: string
    background: string
    surface: string
    link: string
    button: {
      primaryText: string
      secondaryText: string
    }
    header: {
      2: string
    }
    lp: {
      sectionTop: string
    }
    status: {
      approved: string
      rejected: string
      warning: string
      increase: string
      decrease: string
    }
    overlay: {
      scrim: string
    }
    glass: {
      background: string
      border: string
    }
    brand: {
      google: {
        background: string
        text: string
      }
      misskey: {
        background: string
        text: string
      }
    }
    home: {
      screenNameText: string
      screenNameBorder: string
      menuButtonBackground: string
      menuIcon: string
      ctaTrainingLabelStroke: string
      ctaMealLabelStroke: string
    }
    sideDialog: {
      requiredBadgeBackground: string
      optionalBadgeBackground: string
      selectOptionHoverBackground: string
      selectOptionActiveBackground: string
      selectOptionActiveText: string
      selectMenuBackground: string
      selectMenuBorder: string
      multiSelectChipBackground: string
    }
    meals: {
      callout: {
        border: string
        text: string
      }
    }
  }
  gradients: {
    textAccent: string
    logoText: string
    buttonPrimaryFill: string
    buttonPrimaryBorder: string
    lpSection: string
    home: {
      screenNameBackground: string
      ctaTrainingLabel: string
      ctaMealLabel: string
    }
    sideDialog: {
      multiSelectChipSelectedBackground: string
    }
  }
  radii: {
    pill: string
  }
  space: typeof tokens.space
  fontSizes: typeof tokens.fontSizes
  effects: {
    backdropBlur: string
    home: {
      screenNameTextShadow: string
      ctaLabelTextShadow: string
      ctaLabelBackdropBlur: string
    }
    sideDialog: {
      selectMenuBlur: string
      multiSelectChipSelectedShadow: string
      multiSelectChipShadow: string
    }
  }
}

export const lightTheme: AppTheme = {
  colors: {
    text: tokens.colors.neutral.text,
    subText: tokens.colors.neutral.subText,
    background: tokens.colors.surface.base,
    surface: tokens.colors.surface.base,
    link: tokens.colors.link.default,
    button: {
      primaryText: tokens.colors.neutral.white,
      secondaryText: tokens.colors.neutral.text,
    },
    header: {
      2: tokens.gradients.text.brandAccentPink,
    },
    lp: {
      sectionTop: tokens.colors.surface.lpSectionTop,
    },
    status: tokens.colors.status,
    overlay: {
      scrim: 'rgba(200, 200, 200, 0.1)',
    },
    glass: {
      background: 'linear-gradient(183deg, #ffffffa0 0%, #ffffff30 90%);',
      border: 'rgba(255, 255, 255, 0.5)',
    },
    brand: {
      google: {
        background: '#FFFFFF',
        text: '#555', // Google推奨に近い
      },
      misskey: {
        background: '#69BE01',
        text: '#FFFFFF',
      },
    },
    home: {
      screenNameText: '#433741',
      screenNameBorder: '#ffffffaa',
      menuButtonBackground: '#FFFFFFDD',
      menuIcon: '#666666',
      ctaTrainingLabelStroke: '#D59E28',
      ctaMealLabelStroke: '#26C683',
    },
    sideDialog: {
      requiredBadgeBackground: '#FF6F6F',
      optionalBadgeBackground: '#D9D9D9',
      selectOptionHoverBackground: '#fbdfea',
      selectOptionActiveBackground: '#df3b82',
      selectOptionActiveText: '#FFFFFF',
      selectMenuBackground: 'rgba(255, 255, 255, 0.8)',
      selectMenuBorder: '#FFFFFF',
      multiSelectChipBackground: '#fffdfa',
    },
    meals: {
      callout: {
        border: tokens.gradients.text.brandAccentPink,
        text: '#F75B79',
      },
    },
  },
  gradients: {
    textAccent: tokens.gradients.text.brandAccentPink,
    logoText: tokens.gradients.text.brandLogoText,
    buttonPrimaryFill: tokens.gradients.button.primaryFill,
    buttonPrimaryBorder: tokens.gradients.button.primaryBorder,
    lpSection: tokens.gradients.surface.lpSection,
    home: {
      screenNameBackground: 'linear-gradient(90deg, #ffdeec90, #c1ffe790)',
      ctaTrainingLabel: 'linear-gradient(60deg,#c88e1150, #fccd6950)',
      ctaMealLabel: 'linear-gradient(60deg,#4C971250, #C4FC6950)',
    },
    sideDialog: {
      multiSelectChipSelectedBackground: 'linear-gradient(90deg, #fdf7ec, #ffeff6)',
    },
  },
  radii: {
    pill: tokens.radii.pill,
  },
  space: tokens.space,
  fontSizes: tokens.fontSizes,
  effects: {
    backdropBlur: '15px',
    home: {
      screenNameTextShadow: `#fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px,
              #fff 0 0 4px`,
      ctaLabelTextShadow: 'drop-shadow(2px 2px 2px #33333360)',
      ctaLabelBackdropBlur: '2px',
    },
    sideDialog: {
      selectMenuBlur: '5px',
      multiSelectChipSelectedShadow: 'inset -4px 3px 2px rgba(100, 100, 100, 0.3)',
      multiSelectChipShadow: '-2px 2px 3px rgba(0, 0, 0, 0.2)',
    },
  },
}

// 実装時に定義するため全て仮の値
export const darkTheme: AppTheme = {
  colors: {
    ...lightTheme.colors,
    text: '#F5F5F5',
    subText: '#BDBDBD',
    background: '#0F0F12',
    surface: '#17171C',
    link: '#56C7E8',
    status: tokens.colors.status, // 状態色はそのまま流用しても良い（調整は後で）
    overlay: {
      scrim: 'rgba(0, 0, 0, 0.55)',
    },
    glass: {
      background: 'rgba(23, 23, 28, 0.6)',
      border: 'rgba(255, 255, 255, 0.14)',
    },
  },
  gradients: {
    ...lightTheme.gradients,
    // ダークでも同じグラデを使うなら流用、読みにくければダーク専用を作る
    textAccent: tokens.gradients.text.brandAccentPink,
    logoText: tokens.gradients.text.brandLogoText,
    buttonPrimaryFill: tokens.gradients.button.primaryFill,
    buttonPrimaryBorder: tokens.gradients.button.primaryBorder,
  },
  radii: {
    pill: tokens.radii.pill,
  },
  space: tokens.space,
  fontSizes: tokens.fontSizes,
  effects: {
    ...lightTheme.effects,
    backdropBlur: '16px',
  },
}
